<script lang="ts">
// One id prefix per mounted sidebar, so two instances never share a hint id.
let lockUidSeq = 0;
</script>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Component } from 'vue';
import ModuleBadge from './ModuleBadge.vue';
import type { ModuleSwitchTarget, ModuleTeaser } from './ModuleBadge.vue';
import Avatar from '../display/Avatar.vue';
import type { SelectOption } from '../forms/Select.vue';
import type { LumiModuleKey } from '../modules';
import { lumiDockLauncher, toggleLumiDock, setLumiDockLauncherFocus } from './lumiDockLauncher';
import { useFocusTrap } from '../feedback/useOverlayA11y';
import { sidebarCollapse } from './sidebarCollapse';
import LumiIcon from '../icons/LumiIcon.vue';
import LumiChatGlyph from '../icons/LumiChatGlyph.vue';
import Button from '../forms/Button.vue';

/**
 * A nav entry another of the viewer's OWN roles opens (#1028). The entry stays
 * in its place, greyed: hovering or focusing it says which role opens it and
 * where to switch, clicking it offers the switch. component-lib has no i18n, so
 * the host supplies every sentence.
 */
export interface SidebarLockedEntry {
  /** The role the switch button changes to — a `roles` option value. */
  role: string;
  /** Hover/focus hint: which role opens it and where to switch. */
  hint: string;
  /** The popover's sentence ("you have this as X; right now you work as Y"). */
  body: string;
  /** The popover's switch button ("Open as X"). */
  actionLabel: string;
}

export interface SidebarNavItem {
  id: string;
  label: string;
  icon?: Component;
  /** A small glyph after the label — "this page wants attention" (#444). NOT a
   * count and NOT a colour: it renders in `currentColor` at reduced opacity, so it
   * reads as a note rather than an alarm — a page that is merely half-filled has
   * not earned a red dot beside the ones that are fine. The host passes the
   * component; this library has no icon set of its own opinion here. */
  indicator?: Component;
  /** The indicator's accessible name. Required to make one audible — component-lib
   * has no i18n, so the host supplies the sentence. An indicator without it is
   * decorative and hidden from assistive tech, which is the honest default for a
   * glyph nobody can name. */
  indicatorLabel?: string;
  /** How many things on that page wait for the viewer (#548) — a count pill
   * after the label, the same pill the Tabs counters use. Rendered only when
   * above 0: "nothing waiting" is the quiet default, not a zero to read. */
  count?: number;
  /** The count's accessible name ("3 approvals waiting"). component-lib has
   * no i18n, so the host supplies the sentence; without it the bare number is
   * read, which says what, not of what. */
  countLabel?: string;
  /** When set the item renders as a plain anchor to this absolute URL — another
   * app on the suite — instead of a button, and it never emits `navigate`, so
   * the host router is not handed a path it does not own. `id` still keys the
   * row and may match `activeId`. */
  href?: string;
  /** Set when only another of the viewer's roles opens this page (#1028): the
   * entry renders greyed and never emits `navigate`; a click opens the switch
   * popover instead, whose button emits `unlock`. */
  locked?: SidebarLockedEntry;
}

export interface SidebarSection {
  section: string;
  items: SidebarNavItem[];
  icon?: Component;
  /**
   * Accessible name for the CLOSED section header's count pill (the sum of its
   * items' `count`s — "3 open approvals" for a "Führung (3)" header, #556 M4).
   * Same contract as `SidebarNavItem.countLabel`: component-lib has no i18n, so
   * the host supplies the sentence; without it the bare number is read.
   */
  countLabel?: string;
}

export type SidebarItem = SidebarNavItem | SidebarSection;

const props = withDefaults(
  defineProps<{
    /** The module this app is; drives the switcher pill. Unset → no pill at all
     * (the system-admin console is master-scoped and has nothing to switch to). */
    module?: LumiModuleKey;
    /** The module's localized name for the switcher pill (ModuleBadge `label`). */
    moduleLabel?: string;
    /** Glyph override for the pill only (ModuleBadge `icon`). */
    moduleIcon?: Component;
    /** Mute the module switcher and the nav list and make them unclickable (a suspended
     * workspace). Presentation only: the host also empties its targets, so this never
     * stands in for the guard. */
    locked?: boolean;
    logoSrc?: string;
    /** Where the logo navigates — always the workspace across modules. */
    homeHref?: string;
    /** Module switcher targets — when supplied the module badge becomes a switcher. */
    modules?: ModuleSwitchTarget[];
    /** Localized note for the switcher when module access could not be read. */
    moduleAccessNote?: string;
    /** Sellable modules the workspace does not have (issue 261) — a separate,
     * non-navigable group in the launcher. Pass-through to ModuleBadge; see its
     * prop docs for why this is not a flag on `modules`. */
    moduleTeasers?: ModuleTeaser[];
    /** Launcher teaser group heading. Both this and moduleTeaserTagLabel are
     * required for the group to render (no English defaults, same reasoning as
     * moduleSwitcherLabel below). */
    moduleTeaserHeading?: string;
    /** The visible "not in your plan" marker on each teaser. */
    moduleTeaserTagLabel?: string;
    /** Buyers only — label of the link into the billing add flow. */
    moduleTeaserActionLabel?: string;
    /** Non-buyers only — which role can add a module. */
    moduleTeaserNote?: string;
    /** App-launcher overlay heading / aria-label (issue 195). No English default
     * here — the launcher renders it as a visible dialog title, so a hardcoded
     * fallback would silently ship one language to every locale; the caller
     * supplies a translated label. */
    moduleSwitcherLabel?: string;
    /** App-launcher overlay close-button accessible name (issue 195). Same
     * no-default reasoning as moduleSwitcherLabel above. */
    moduleSwitcherCloseLabel?: string;
    /** Label over the modules below Home in the launcher (#1039). Pass-through
     * to ModuleBadge; no English default, same reasoning as moduleSwitcherLabel. */
    moduleListHeading?: string;
    items?: SidebarItem[];
    activeId?: string;
    /** In-app history (spec §4): the two arrows beside the logo. Forward is
     * dimmed when there is nothing ahead; the host answers `back`/`forward`. */
    canGoBack?: boolean;
    canGoForward?: boolean;
    backLabel?: string;
    forwardLabel?: string;
    /** Account row identity: the display name (falls back to the email) and the
     * email, which the menu head shows beneath the name. */
    name?: string;
    email?: string;
    /** What the account row says when there is neither a name nor an email — a
     * phone-only account (issue 698, ADR 0093) has no address. The host passes
     * its localized "Signed in"; without it the row would be blank. */
    signedInLabel?: string;
    photoUrl?: string;
    /** Current role label (read-only) when there is nothing to switch. */
    roleLabel?: string;
    /** Role switcher options; when more than one the menu offers them inline. */
    roles?: SelectOption[];
    activeRole?: string;
    /** Language preference. */
    locale?: string;
    locales?: SelectOption[];
    /** Theme preference — 'light' | 'dark'. When unset the entry is hidden. */
    theme?: string;
    themeMenuLabel?: string;
    themeLightLabel?: string;
    themeDarkLabel?: string;
    /** Mein Profil — the person's own record (people-ui `/me`). The CALLER gates
     * it on People access; unset → no entry. */
    profileHref?: string;
    profileLabel?: string;
    /** Einstellungen ↗ — the tenant admin app. The CALLER gates it on the tenant
     * admin flag; unset → no entry. */
    settingsHref?: string;
    settingsLabel?: string;
    /** Support entry in the account menu. */
    supportLabel?: string;
    /** Notifications bell label + unread count. When notificationsLabel is
     * unset the bell is hidden (opt-in per app). */
    notificationsLabel?: string;
    /** `null` means "not yet known" (e.g. the first summary fetch hasn't
     * resolved or failed silently) — distinct from a confirmed `0`. Renders a
     * quiet, non-alarming marker instead of the count, so a load failure can
     * never look identical to an empty inbox (issue 195, DR-20b). */
    notificationsCount?: number | null;
    /** Accessible name for the "count unavailable" marker. No default:
     * component-lib has no i18n of its own, so a hardcoded string here would
     * announce one language to every locale (issue 195, DR-19a/DR-20b). A
     * caller that renders the marker supplies a translated label; when it is
     * unset the marker still renders, just with no accessible name. */
    notificationsUnknownLabel?: string;
    logoutLabel?: string;
    /** Sign-out is offered elsewhere (a demo tenant's stage bar carries it, #409
     * follow-ups F13): the entry stays visible but does nothing and its native
     * tooltip says where to go. `aria-disabled` rather than `disabled` so the
     * tooltip is actually shown — browsers suppress hover on a disabled button. */
    logoutDisabled?: boolean;
    /** The tooltip while `logoutDisabled`; falls back to `logoutLabel`. */
    logoutHint?: string;
    roleMenuLabel?: string;
    languageMenuLabel?: string;
    /** Accessible name of the account row button. */
    accountLabel?: string;
    /** The locked-entry popover's footer: where the role is switched any time
     * (#1028). Unset → no footer. */
    lockedWhereLabel?: string;
    /** The locked-entry popover's close button. */
    lockedCloseLabel?: string;
  }>(),
  {
    items: () => [],
    canGoBack: false,
    canGoForward: false,
    backLabel: 'Back',
    forwardLabel: 'Forward',
    notificationsCount: 0,
    // Neutral English on purpose (#388): every app passes its own translated
    // labels, so a sidebar that shows one of these exact strings is a caller
    // that forgot — and a forgotten label should read as wrong, not as default.
    supportLabel: 'Support',
    profileLabel: 'My profile',
    settingsLabel: 'Settings',
    logoutLabel: 'Logout',
    logoutDisabled: false,
    roleMenuLabel: 'Role',
    languageMenuLabel: 'Language',
    themeMenuLabel: 'Theme',
    themeLightLabel: 'Light',
    themeDarkLabel: 'Dark',
    accountLabel: 'Account',
    lockedCloseLabel: 'Close',
  },
);

const emit = defineEmits<{
  navigate: [id: string];
  home: [];
  back: [];
  forward: [];
  logout: [];
  support: [];
  notifications: [];
  /**
   * The account menu's profile entry was activated. The entry stays a plain
   * anchor to `profileHref` (a cross-origin URL in most apps); the app that
   * owns the profile route can `preventDefault()` here and push it on its own
   * router instead of reloading itself.
   */
  profile: [event: MouseEvent];
  /**
   * The account menu's settings entry was activated. Same contract as `profile`
   * above, and it exists for the same reason: since #432 tenant administration is
   * a section of the WORKSPACE's own nav, so in that one app the entry points at a
   * route the app itself owns and should be pushed rather than reloaded. Every
   * other app keeps it a plain cross-origin anchor and never listens.
   */
  settings: [event: MouseEvent];
  'update:role': [value: string];
  'update:locale': [value: string];
  'update:theme': [value: string];
  /** The locked-entry popover's switch button (#1028): change to `role` and
   * open the entry `id`. The host switches; the sidebar only asks. */
  unlock: [id: string, role: string];
}>();

function isSection(item: SidebarItem): item is SidebarSection {
  return 'section' in item;
}

/**
 * A CLOSED section's header carries the sum of its items' counts, as the same
 * pill the items themselves use — an OPEN section shows nothing (its items
 * carry their own pills already), and a zero sum shows nothing either (#556
 * M4). Nested sections do not exist (one level, per the type), so this never
 * recurses.
 */
function sectionCountSum(item: SidebarSection): number {
  return item.items.reduce((sum, child) => sum + (child.count ?? 0), 0);
}

// The logo is a CSS mask filled with `--logo-color` (espresso on light, sand on
// dark, spec §4): one black-on-transparent SVG serves both themes.
const logoMask = computed(() => (props.logoSrc ? `url("${props.logoSrc}")` : undefined));

const displayName = computed(() => props.name || props.email || props.signedInLabel || '');
const roleSwitchable = computed(() => Array.isArray(props.roles) && props.roles.length > 1);
const currentRoleLabel = computed(() => {
  if (roleSwitchable.value) {
    return props.roles?.find((r) => String(r.value) === String(props.activeRole))?.label ?? props.roleLabel ?? '';
  }
  return props.roleLabel ?? '';
});
const currentLocaleLabel = computed(
  () => props.locales?.find((l) => String(l.value) === String(props.locale))?.label ?? props.locale ?? '',
);
const currentThemeLabel = computed(() => (props.theme === 'dark' ? props.themeDarkLabel : props.themeLightLabel));

// ── Accordion sections — one open at a time, all closed by default ──
const openSection = ref<number | null>(null);
function sectionContainsActive(item: SidebarSection): boolean {
  return !!props.activeId && item.items.some((n) => n.id === props.activeId);
}
// Open the section holding the active route on load / when it changes.
watch(
  () => props.activeId,
  () => {
    props.items.forEach((it, i) => {
      if (isSection(it) && sectionContainsActive(it)) openSection.value = i;
    });
  },
  { immediate: true },
);
function toggleSection(i: number) {
  openSection.value = openSection.value === i ? null : i;
}

// ── Account menu. Teleported to <body> (issue 364/F50): an in-flow backdrop
// nested inside the sidebar never covered the viewport (the sidebar used to be
// a containing block for fixed descendants), so the menu computes its own
// position off the account button's rect — the same recipe the notifications
// panel below uses. Opens above the row, offset to the right so it overlaps
// the seam between the sidebar and the content panel (spec §4). ──
const menuOpen = ref(false);
const avatarBtnRef = ref<HTMLElement | null>(null);
const menuContainerRef = ref<HTMLElement | null>(null);
const menuPos = ref({ top: 0, left: 0 });
const { isTopmost: isMenuTopmost } = useFocusTrap(menuOpen, menuContainerRef);
// The one disclosure (role or language list) open inside the menu, if any.
const openGroup = ref<'role' | 'locale' | null>(null);

// The two account-row popovers open from a button near the sidebar's foot. On a
// phone that row sits in the drawer, most of a panel's width from the screen's
// right edge, so each panel is slid back inside the viewport where its anchor
// would run it past the edge. Their CSS caps the width at the viewport less
// VIEWPORT_EDGE a side, the same margin kept clear here.
const VIEWPORT_EDGE = 12;
const MENU_W = 236;
const NOTIF_PANEL_W = 360;
function clampLeft(left: number, width: number): number {
  const w = Math.min(width, window.innerWidth - 2 * VIEWPORT_EDGE);
  return Math.max(VIEWPORT_EDGE, Math.min(left, window.innerWidth - VIEWPORT_EDGE - w));
}

function updateMenuPosition() {
  const btn = avatarBtnRef.value;
  if (!btn) return;
  const rect = btn.getBoundingClientRect();
  menuPos.value = { top: rect.top - 8, left: clampLeft(rect.left + Math.round(rect.width * 0.55), MENU_W) };
}

function onMenuViewportChange() {
  if (menuOpen.value) updateMenuPosition();
}

function toggleMenu() {
  if (menuOpen.value) {
    closeMenu();
    return;
  }
  menuOpen.value = true;
  openGroup.value = null;
  updateMenuPosition();
  window.addEventListener('resize', onMenuViewportChange);
  window.addEventListener('scroll', onMenuViewportChange, true);
}

function closeMenu() {
  menuOpen.value = false;
  openGroup.value = null;
  window.removeEventListener('resize', onMenuViewportChange);
  window.removeEventListener('scroll', onMenuViewportChange, true);
}

function onProfile(e: MouseEvent) {
  emit('profile', e);
  closeMenu();
}

function onSettings(e: MouseEvent) {
  emit('settings', e);
  closeMenu();
}

// The "opens elsewhere" glyph, only when it actually does. Every module UI links
// Einstellungen into another origin and earns the mark; the workspace, which owns
// those pages since #432, does not — and an arrow promising a new tab on a link
// that pushes the current router is a small lie the menu can avoid telling. A
// relative or unparseable href is treated as same-origin: the caller that passed
// it is the app itself.
const settingsIsExternal = computed(() => {
  const href = String(props.settingsHref || '').trim();
  if (!href || typeof window === 'undefined') return false;
  try {
    return new URL(href, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
});

function onMenuKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isMenuTopmost()) closeMenu();
}
watch(menuOpen, (open) => {
  if (typeof document === 'undefined') return;
  if (open) document.addEventListener('keydown', onMenuKeydown);
  else document.removeEventListener('keydown', onMenuKeydown);
});

function toggleGroup(group: 'role' | 'locale') {
  openGroup.value = openGroup.value === group ? null : group;
}
function onMenuRole(v: string | number) {
  if (v === '') return;
  emit('update:role', String(v));
  closeMenu();
}
function onMenuLocale(v: string | number) {
  emit('update:locale', String(v));
  closeMenu();
}
// Design: a two-way toggle, so the row itself flips it and shows the current value.
function onToggleTheme() {
  emit('update:theme', props.theme === 'dark' ? 'light' : 'dark');
}
function onSupport() {
  closeMenu();
  emit('support');
}
// Disabled sign-out neither emits nor closes the menu: the tooltip is the answer.
function onLogout() {
  if (props.logoutDisabled) return;
  closeMenu();
  emit('logout');
}

// ── Notifications popover. Same teleport-to-<body> reasoning as the account
// menu above: an in-flow `position:absolute` panel gets clipped by the nearest
// `overflow` ancestor. Anchored off the bell's rect, recalculated whenever it
// opens or the viewport changes under it. ──
const notifOpen = ref(false);
const notifBtnRef = ref<HTMLElement | null>(null);
const notifContainerRef = ref<HTMLElement | null>(null);
const notifPanelPos = ref({ top: 0, left: 0 });
const { isTopmost: isNotifTopmost } = useFocusTrap(notifOpen, notifContainerRef);

function updateNotifPosition() {
  const btn = notifBtnRef.value;
  if (!btn) return;
  const rect = btn.getBoundingClientRect();
  notifPanelPos.value = { top: rect.top - 8, left: clampLeft(rect.left, NOTIF_PANEL_W) };
}

function onViewportChange() {
  if (notifOpen.value) updateNotifPosition();
}

function toggleNotif() {
  if (notifOpen.value) {
    closeNotif();
    return;
  }
  notifOpen.value = true;
  emit('notifications');
  updateNotifPosition();
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, true);
}

function closeNotif() {
  notifOpen.value = false;
  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('scroll', onViewportChange, true);
}

function onNotifKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isNotifTopmost()) closeNotif();
}
watch(notifOpen, (open) => {
  if (typeof document === 'undefined') return;
  if (open) document.addEventListener('keydown', onNotifKeydown);
  else document.removeEventListener('keydown', onNotifKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onMenuViewportChange);
  window.removeEventListener('scroll', onMenuViewportChange, true);
  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('scroll', onViewportChange, true);
  if (typeof document !== 'undefined') {
    document.removeEventListener('keydown', onMenuKeydown);
    document.removeEventListener('keydown', onNotifKeydown);
  }
});

// ── The folded rail (#540). AppShell's seam handle flips `sidebarCollapse`;
// the rail only exists on desktop — the mobile drawer always shows the full
// sidebar, whatever the remembered state says. ──
const isDesktop = ref(true);
let desktopQuery: MediaQueryList | null = null;
function onDesktopQueryChange() {
  isDesktop.value = !!desktopQuery?.matches;
}
onMounted(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  desktopQuery = window.matchMedia('(min-width: 768px)');
  onDesktopQueryChange();
  desktopQuery.addEventListener('change', onDesktopQueryChange);
});
const rail = computed(() => sidebarCollapse.collapsed && isDesktop.value);

/** A rail row with no icon of its own still needs something to click. */
function initial(label: string): string {
  return label.trim().charAt(0).toUpperCase();
}

// In the rail a section is one icon; its pages open in a flyout beside it,
// teleported to <body> and anchored off the button like the account menu.
const firstSectionIndex = computed(() => props.items.findIndex((it) => isSection(it)));
const flyoutIndex = ref<number | null>(null);
const flyoutSection = computed(() => {
  const item = flyoutIndex.value === null ? undefined : props.items[flyoutIndex.value];
  return item && isSection(item) ? item : null;
});
const flyoutOpen = computed(() => flyoutSection.value !== null);
const flyoutRef = ref<HTMLElement | null>(null);
const flyoutPos = ref({ top: 0, left: 0 });
const { isTopmost: isFlyoutTopmost } = useFocusTrap(flyoutOpen, flyoutRef);

async function toggleFlyout(i: number, e: MouseEvent) {
  if (flyoutIndex.value === i) {
    closeFlyout();
    return;
  }
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  flyoutIndex.value = i;
  flyoutPos.value = { top: rect.top - 8, left: rect.right + 8 };
  await nextTick();
  // Keep a section near the foot on screen: lift it by what would overflow.
  const height = flyoutRef.value?.offsetHeight ?? 0;
  const maxTop = window.innerHeight - height - 8;
  if (flyoutPos.value.top > maxTop) flyoutPos.value = { ...flyoutPos.value, top: Math.max(8, maxTop) };
}
function closeFlyout() {
  flyoutIndex.value = null;
}
function onFlyoutNavigate(id: string) {
  closeFlyout();
  emit('navigate', id);
}
function onFlyoutKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isFlyoutTopmost()) closeFlyout();
}
watch(flyoutOpen, (open) => {
  if (typeof document === 'undefined') return;
  if (open) document.addEventListener('keydown', onFlyoutKeydown);
  else document.removeEventListener('keydown', onFlyoutKeydown);
});
watch([rail, () => props.activeId], closeFlyout);
onBeforeUnmount(() => {
  desktopQuery?.removeEventListener('change', onDesktopQueryChange);
  if (typeof document !== 'undefined') document.removeEventListener('keydown', onFlyoutKeydown);
});

// ── Locked entries (#1028): a page another of the viewer's own roles opens.
// Hover or focus shows the hint beside the entry; a click opens a small dialog
// with the switch button. Both are teleported to <body> and anchored off the
// entry's rect, the same recipe as the account menu. The hint is a visual echo
// of the entry's `aria-describedby` text, so it is hidden from assistive tech. ──
const lockUid = `lumi-sidebar-lock-${++lockUidSeq}`;
// The rail flyout renders a second copy of a section's entries while the inline
// group stays in the DOM (`v-show`), so each copy names its hint apart.
function lockHintId(id: string, copy: 'nav' | 'flyout' = 'nav'): string {
  return `${lockUid}-${copy}-${id.replace(/[^A-Za-z0-9_-]/g, '_')}`;
}
const lockHint = ref<{ text: string; top: number; left: number } | null>(null);
// Closing the dialog hands focus back to the entry; that focus must not pop the
// hint straight back over what the person just dismissed.
let suppressNextFocusHint = false;
function showLockHint(item: SidebarNavItem, e: Event) {
  if (e.type === 'focus' && suppressNextFocusHint) {
    suppressNextFocusHint = false;
    return;
  }
  if (!item.locked || lockItem.value) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  lockHint.value = { text: item.locked.hint, top: rect.top + rect.height / 2, left: rect.right + 8 };
  lockHintFor.value = item.id;
}
function hideLockHint() {
  lockHint.value = null;
  lockHintFor.value = null;
}

const sidebarRef = ref<HTMLElement | null>(null);
const lockItem = ref<SidebarNavItem | null>(null);
const lockOpen = computed(() => lockItem.value !== null);
const lockRef = ref<HTMLElement | null>(null);
const lockPos = ref({ top: 0, left: 0 });
const accountPulse = ref(false);
let accountPulseTimer: ReturnType<typeof setTimeout> | null = null;
const { isTopmost: isLockTopmost } = useFocusTrap(lockOpen, lockRef);

async function openLock(item: SidebarNavItem, e: MouseEvent) {
  if (!item.locked) return;
  hideLockHint();
  if (lockItem.value?.id === item.id) {
    closeLock();
    return;
  }
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  // From the rail flyout: the flyout (and the button that had focus) goes away,
  // so focus moves to the section button that opened it first — the dialog's
  // focus trap returns there when it closes, not to <body>.
  if (flyoutOpen.value) {
    const trigger = sidebarRef.value?.querySelector<HTMLElement>('.lumi-sidebar__rail-section--open');
    closeFlyout();
    trigger?.focus();
  }
  lockItem.value = item;
  lockPos.value = { top: rect.top - 8, left: rect.right + 8 };
  // The account row is where the switch lives any time; one pulse points at it.
  accountPulse.value = false;
  if (accountPulseTimer) clearTimeout(accountPulseTimer);
  await nextTick();
  accountPulse.value = true;
  accountPulseTimer = setTimeout(() => (accountPulse.value = false), 1600);
  // Keep an entry near the foot on screen: lift the dialog by what would overflow.
  const height = lockRef.value?.offsetHeight ?? 0;
  const maxTop = window.innerHeight - height - 8;
  if (lockPos.value.top > maxTop) lockPos.value = { ...lockPos.value, top: Math.max(8, maxTop) };
}
function closeLock() {
  if (!lockItem.value) return;
  suppressNextFocusHint = true;
  lockItem.value = null;
  // Focus may land somewhere without a focus event (the rail's section button);
  // the flag must not outlive this close.
  setTimeout(() => (suppressNextFocusHint = false), 0);
}
function onUnlock() {
  const item = lockItem.value;
  if (!item?.locked) return;
  closeLock();
  emit('unlock', item.id, item.locked.role);
}
function onLockKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isLockTopmost()) closeLock();
}
watch(lockOpen, (open) => {
  if (typeof document === 'undefined') return;
  if (open) document.addEventListener('keydown', onLockKeydown);
  else document.removeEventListener('keydown', onLockKeydown);
});
// The hosts rebuild `items` on every count refresh or late gate answer; the
// dialog and hint close only when their entry is no longer a locked one.
function lockedEntryStill(id: string): boolean {
  return props.items.some((it) =>
    isSection(it) ? it.items.some((child) => child.id === id && !!child.locked) : it.id === id && !!it.locked,
  );
}
const lockHintFor = ref<string | null>(null);
watch(
  () => props.items,
  () => {
    if (lockHintFor.value && !lockedEntryStill(lockHintFor.value)) hideLockHint();
    if (lockItem.value && !lockedEntryStill(lockItem.value.id)) closeLock();
  },
);
onBeforeUnmount(() => {
  if (accountPulseTimer) clearTimeout(accountPulseTimer);
  if (typeof document !== 'undefined') document.removeEventListener('keydown', onLockKeydown);
});

// Lumi's one entry point (make brief ai-switch-notify-launcher, outcome
// invariants 9-10): unlike the bell above, this button has no panel of its own —
// clicking it toggles the LumiShell instance mounted elsewhere in the page (the
// `#dock` slot), reached through the lumiDockLauncher bridge because Sidebar and
// LumiShell are unrelated siblings in every AppChrome host. It stays put while
// Lumi is open, so the same button that opened it closes it again.
const lumiDockBtnRef = ref<HTMLElement | null>(null);
onMounted(() => {
  setLumiDockLauncherFocus(() => lumiDockBtnRef.value?.focus());
});
onBeforeUnmount(() => {
  setLumiDockLauncherFocus(null);
});
</script>

<template>
  <aside ref="sidebarRef" class="lumi-sidebar" :class="{ 'lumi-sidebar--rail': rail, 'lumi-sidebar--locked': locked }" :data-module="module">
    <!-- Logo row: the mark at 26px, and at the right end the two in-app history
         arrows (spec §4). -->
    <div class="lumi-sidebar__logo">
      <component
        :is="homeHref ? 'a' : 'button'"
        :href="homeHref || undefined"
        :type="homeHref ? undefined : 'button'"
        class="lumi-sidebar__home"
        aria-label="Workspace"
        @click="!homeHref && emit('home')"
      >
        <span v-if="logoSrc" class="lumi-sidebar__mark" :style="{ maskImage: logoMask, WebkitMaskImage: logoMask }" role="img" aria-label="lumi-HR" />
        <strong v-else class="lumi-sidebar__wordmark">lumi<span class="lumi-sidebar__dot">·</span>hr</strong>
      </component>
      <div class="lumi-sidebar__history">
        <button
          type="button"
          class="lumi-sidebar__history-btn"
          :disabled="!canGoBack"
          :aria-label="backLabel"
          :title="backLabel"
          @click="emit('back')"
        >
          <LumiIcon name="previous" width="16" height="16" stroke-width="2" />
        </button>
        <button
          type="button"
          class="lumi-sidebar__history-btn"
          :disabled="!canGoForward"
          :aria-label="forwardLabel"
          :title="forwardLabel"
          @click="emit('forward')"
        >
          <LumiIcon name="next" width="16" height="16" stroke-width="2" />
        </button>
      </div>
    </div>

    <div v-if="module" class="lumi-sidebar__badge">
      <ModuleBadge
        :module="module"
        :label="moduleLabel"
        :icon="moduleIcon"
        :modules="modules"
        :access-note="moduleAccessNote"
        :teasers="moduleTeasers"
        :teaser-heading="moduleTeaserHeading"
        :teaser-tag-label="moduleTeaserTagLabel"
        :teaser-action-label="moduleTeaserActionLabel"
        :teaser-note="moduleTeaserNote"
        :switcher-label="moduleSwitcherLabel"
        :close-label="moduleSwitcherCloseLabel"
        :module-list-heading="moduleListHeading"
      />
    </div>

    <nav class="lumi-sidebar__nav">
      <template v-for="(item, i) in items" :key="isSection(item) ? `s${i}` : item.id">
        <!-- Section -->
        <template v-if="isSection(item) && rail">
          <span v-if="i === firstSectionIndex" class="lumi-sidebar__rail-divider" aria-hidden="true" />
          <button
            type="button"
            class="lumi-sidebar__item lumi-sidebar__rail-section"
            :class="{ 'lumi-sidebar__item--active': sectionContainsActive(item), 'lumi-sidebar__rail-section--open': flyoutIndex === i }"
            :title="item.section"
            :aria-label="item.section"
            aria-haspopup="menu"
            :aria-expanded="flyoutIndex === i"
            @click="toggleFlyout(i, $event)"
          >
            <component :is="item.icon" v-if="item.icon" />
            <LumiIcon v-else name="section" width="18" height="18" stroke-width="2" />
            <!-- The rail already puts an item's own count in this absolute-positioned
                 corner badge (see `.lumi-sidebar--rail .lumi-sidebar__count`); the
                 section glyph gets the same sum badge a closed header shows (#556 M4). -->
            <span v-if="sectionCountSum(item) > 0" class="lumi-sidebar__count" data-test="sidebar-count"><span :aria-hidden="item.countLabel ? 'true' : undefined">{{ sectionCountSum(item) }}</span><span v-if="item.countLabel" class="lumi-sidebar__sr">{{ item.countLabel }}</span></span>
          </button>
        </template>
        <template v-else-if="isSection(item)">
          <button
            type="button"
            class="lumi-sidebar__section"
            :class="{ 'lumi-sidebar__section--open': openSection === i }"
            :aria-expanded="openSection === i"
            @click="toggleSection(i)"
          >
            <span>{{ item.section }}</span>
            <span class="lumi-sidebar__section-end">
              <!-- Closed: the sum of the section's items' counts, in the same pill
                   the items use. Open: nothing — the items already carry their own
                   pills (#556 M4). Zero renders nothing either way. -->
              <span v-if="openSection !== i && sectionCountSum(item) > 0" class="lumi-sidebar__count" data-test="sidebar-count"><span :aria-hidden="item.countLabel ? 'true' : undefined">{{ sectionCountSum(item) }}</span><span v-if="item.countLabel" class="lumi-sidebar__sr">{{ item.countLabel }}</span></span>
              <LumiIcon name="expand" class="lumi-sidebar__chevron" width="14" height="14" stroke-width="2.5" />
            </span>
          </button>
          <div v-show="openSection === i" class="lumi-sidebar__group">
            <template v-for="child in item.items" :key="child.id">
              <button
                v-if="child.locked"
                type="button"
                class="lumi-sidebar__item lumi-sidebar__item--locked"
                aria-haspopup="dialog"
                :aria-expanded="lockItem?.id === child.id"
                :aria-describedby="lockHintId(child.id)"
                data-test="sidebar-locked"
                @mouseenter="showLockHint(child, $event)"
                @mouseleave="hideLockHint"
                @focus="showLockHint(child, $event)"
                @blur="hideLockHint"
                @click="openLock(child, $event)"
              >
                <component :is="child.icon" v-if="child.icon" />
                <span class="lumi-sidebar__label">{{ child.label }}</span>
                <span :id="lockHintId(child.id)" hidden>{{ child.locked.hint }}</span>
              </button>
              <a
                v-else-if="child.href"
                :href="child.href"
                class="lumi-sidebar__item"
                :class="{ 'lumi-sidebar__item--active': child.id === activeId }"
                :aria-current="child.id === activeId ? 'page' : undefined"
                rel="noopener"
              >
                <component :is="child.icon" v-if="child.icon" />
                <span class="lumi-sidebar__label">{{ child.label }}</span>
                <component
                  :is="child.indicator"
                  v-if="child.indicator"
                  class="lumi-sidebar__indicator"
                  :aria-label="child.indicatorLabel || undefined"
                  :aria-hidden="child.indicatorLabel ? undefined : 'true'"
                  :role="child.indicatorLabel ? 'img' : undefined"
                />
                <span v-if="(child.count ?? 0) > 0" class="lumi-sidebar__count" data-test="sidebar-count"><span :aria-hidden="child.countLabel ? 'true' : undefined">{{ child.count }}</span><span v-if="child.countLabel" class="lumi-sidebar__sr">{{ child.countLabel }}</span></span>
              </a>
              <button
                v-else
                type="button"
                class="lumi-sidebar__item"
                :class="{ 'lumi-sidebar__item--active': child.id === activeId }"
                :aria-current="child.id === activeId ? 'page' : undefined"
                @click="emit('navigate', child.id)"
              >
                <component :is="child.icon" v-if="child.icon" />
                <span class="lumi-sidebar__label">{{ child.label }}</span>
                <component
                  :is="child.indicator"
                  v-if="child.indicator"
                  class="lumi-sidebar__indicator"
                  :aria-label="child.indicatorLabel || undefined"
                  :aria-hidden="child.indicatorLabel ? undefined : 'true'"
                  :role="child.indicatorLabel ? 'img' : undefined"
                />
                <span v-if="(child.count ?? 0) > 0" class="lumi-sidebar__count" data-test="sidebar-count"><span :aria-hidden="child.countLabel ? 'true' : undefined">{{ child.count }}</span><span v-if="child.countLabel" class="lumi-sidebar__sr">{{ child.countLabel }}</span></span>
              </button>
            </template>
          </div>
        </template>

        <!-- An entry another of the viewer's roles opens (#1028): greyed in its
             place; a click offers the switch instead of navigating. -->
        <button
          v-else-if="item.locked"
          type="button"
          class="lumi-sidebar__item lumi-sidebar__item--locked"
          aria-haspopup="dialog"
          :aria-expanded="lockItem?.id === item.id"
          :aria-describedby="lockHintId(item.id)"
          data-test="sidebar-locked"
          @mouseenter="showLockHint(item, $event)"
          @mouseleave="hideLockHint"
          @focus="showLockHint(item, $event)"
          @blur="hideLockHint"
          @click="openLock(item, $event)"
        >
          <component :is="item.icon" v-if="item.icon" />
          <span v-else-if="rail" class="lumi-sidebar__initial" aria-hidden="true">{{ initial(item.label) }}</span>
          <span class="lumi-sidebar__label">{{ item.label }}</span>
          <span :id="lockHintId(item.id)" hidden>{{ item.locked.hint }}</span>
        </button>
        <!-- Top-level link to another suite app: same class and styling as the
             button below; `rel="noopener"` because the target is another origin
             in local dev. -->
        <a
          v-else-if="item.href"
          :href="item.href"
          class="lumi-sidebar__item"
          :class="{ 'lumi-sidebar__item--active': item.id === activeId }"
          :aria-current="item.id === activeId ? 'page' : undefined"
          :title="rail ? item.label : undefined"
          rel="noopener"
        >
          <component :is="item.icon" v-if="item.icon" />
          <span v-else-if="rail" class="lumi-sidebar__initial" aria-hidden="true">{{ initial(item.label) }}</span>
          <span class="lumi-sidebar__label">{{ item.label }}</span>
          <component
            :is="item.indicator"
            v-if="item.indicator"
            class="lumi-sidebar__indicator"
            :aria-label="item.indicatorLabel || undefined"
            :aria-hidden="item.indicatorLabel ? undefined : 'true'"
            :role="item.indicatorLabel ? 'img' : undefined"
          />
          <span v-if="(item.count ?? 0) > 0" class="lumi-sidebar__count" data-test="sidebar-count"><span :aria-hidden="item.countLabel ? 'true' : undefined">{{ item.count }}</span><span v-if="item.countLabel" class="lumi-sidebar__sr">{{ item.countLabel }}</span></span>
        </a>
        <!-- Top-level item -->
        <button
          v-else
          type="button"
          class="lumi-sidebar__item"
          :class="{ 'lumi-sidebar__item--active': item.id === activeId }"
          :aria-current="item.id === activeId ? 'page' : undefined"
          :title="rail ? item.label : undefined"
          @click="emit('navigate', item.id)"
        >
          <component :is="item.icon" v-if="item.icon" />
          <span v-else-if="rail" class="lumi-sidebar__initial" aria-hidden="true">{{ initial(item.label) }}</span>
          <span class="lumi-sidebar__label">{{ item.label }}</span>
          <component
            :is="item.indicator"
            v-if="item.indicator"
            class="lumi-sidebar__indicator"
            :aria-label="item.indicatorLabel || undefined"
            :aria-hidden="item.indicatorLabel ? undefined : 'true'"
            :role="item.indicatorLabel ? 'img' : undefined"
          />
          <span v-if="(item.count ?? 0) > 0" class="lumi-sidebar__count" data-test="sidebar-count"><span :aria-hidden="item.countLabel ? 'true' : undefined">{{ item.count }}</span><span v-if="item.countLabel" class="lumi-sidebar__sr">{{ item.countLabel }}</span></span>
        </button>
      </template>
    </nav>

    <!-- Foot = the account row only (spec §4): avatar on the nav-icon axis, name
         on the label axis with the role beneath, then the bell and Lumi as icon
         buttons, Lumi outermost. -->
    <div class="lumi-sidebar__footer">
      <div v-if="$slots['footer-extra'] && !rail" class="lumi-sidebar__footer-extra">
        <slot name="footer-extra" />
      </div>

      <div class="lumi-sidebar__account-row">
        <button
          ref="avatarBtnRef"
          type="button"
          class="lumi-sidebar__avatar-btn"
          :class="{ 'lumi-sidebar__avatar-btn--pulse': accountPulse }"
          :aria-haspopup="true"
          :aria-expanded="menuOpen"
          :aria-label="accountLabel"
          :title="displayName"
          @click="toggleMenu"
        >
          <Avatar :name="displayName || '?'" :src="photoUrl" size="md" self />
          <span class="lumi-sidebar__account-text">
            <span class="lumi-sidebar__account-name">{{ displayName }}</span>
            <span v-if="currentRoleLabel" class="lumi-sidebar__account-role">{{ currentRoleLabel }}</span>
          </span>
        </button>

        <div v-if="notificationsLabel" class="lumi-sidebar__notifications">
          <button
            ref="notifBtnRef"
            type="button"
            class="lumi-sidebar__icon-btn"
            :title="notificationsLabel"
            :aria-label="notificationsLabel"
            :aria-haspopup="true"
            :aria-expanded="notifOpen"
            @click="toggleNotif"
          >
            <span class="lumi-sidebar__notif-icon">
              <LumiIcon name="notification" width="18" height="18" stroke-width="2" />
              <!-- Unread: the coral count (spec §2 — Koralle shows, it does not judge). -->
              <span v-if="notificationsCount !== null && notificationsCount > 0" class="lumi-sidebar__notif-badge">
                {{ notificationsCount > 99 ? '99+' : notificationsCount }}
              </span>
              <!-- "Not yet known" (null): quiet dot, not the coral count — "we don't
                   know" must never read as "you have messages". role="status" so the
                   marker announces itself independently of the button's own
                   aria-label, the same nested-live-region idiom Spinner uses inside
                   Button/IconButton. -->
              <span
                v-else-if="notificationsCount === null"
                class="lumi-sidebar__notif-badge lumi-sidebar__notif-badge--unknown"
                role="status"
                :aria-label="notificationsUnknownLabel"
              />
            </span>
          </button>

          <Teleport to="body">
            <div v-if="notifOpen" class="lumi-sidebar__menu-backdrop" @click="closeNotif" />
            <div
              v-if="notifOpen"
              ref="notifContainerRef"
              class="lumi-sidebar__notif-panel"
              role="dialog"
              tabindex="-1"
              :aria-label="notificationsLabel"
              :style="{ top: `${notifPanelPos.top}px`, left: `${notifPanelPos.left}px` }"
            >
              <!-- `close` is the panel's one door back into the bell's state: a
                   notification that navigates in-app (router push, RouterLink) shuts
                   the popover it was clicked in, where a cross-origin navigation
                   unloads the page and never needed to. closeNotif itself stays
                   private (issue 391). -->
              <slot name="notifications-panel" :close="closeNotif" />
            </div>
          </Teleport>
        </div>

        <!-- Lumi's one entry point (outcome invariants 9-10). Rendered whenever a
             LumiShell is mounted, open or closed: `aria-expanded` says which, and
             a second click closes it. -->
        <button
          v-if="lumiDockLauncher.visible"
          ref="lumiDockBtnRef"
          type="button"
          class="lumi-sidebar__icon-btn lumi-sidebar__lumi-btn"
          :class="{ 'lumi-sidebar__lumi-btn--open': lumiDockLauncher.expanded }"
          :title="lumiDockLauncher.label"
          :aria-label="lumiDockLauncher.label"
          :aria-expanded="lumiDockLauncher.expanded"
          :aria-busy="lumiDockLauncher.working || undefined"
          @click="toggleLumiDock"
        >
          <LumiChatGlyph class="lumi-sidebar__lumi-icon" :open="lumiDockLauncher.expanded" :working="lumiDockLauncher.working" />
        </button>
      </div>
    </div>

    <!-- A rail section's pages (#540), teleported to <body> — see `flyoutIndex`. -->
    <Teleport to="body">
      <div v-if="flyoutSection" class="lumi-sidebar__menu-backdrop" @click="closeFlyout" />
      <div
        v-if="flyoutSection"
        ref="flyoutRef"
        class="lumi-sidebar__flyout"
        role="menu"
        tabindex="-1"
        :aria-label="flyoutSection.section"
        :style="{ top: `${flyoutPos.top}px`, left: `${flyoutPos.left}px` }"
      >
        <div class="lumi-sidebar__flyout-head">{{ flyoutSection.section }}</div>
        <template v-for="child in flyoutSection.items" :key="child.id">
          <button
            v-if="child.locked"
            type="button"
            class="lumi-sidebar__menu-item lumi-sidebar__menu-item--locked"
            role="menuitem"
            aria-haspopup="dialog"
            :aria-describedby="lockHintId(child.id, 'flyout')"
            data-test="sidebar-locked"
            @mouseenter="showLockHint(child, $event)"
            @mouseleave="hideLockHint"
            @focus="showLockHint(child, $event)"
            @blur="hideLockHint"
            @click="openLock(child, $event)"
          >
            <component :is="child.icon" v-if="child.icon" class="lumi-sidebar__menu-glyph" aria-hidden="true" />
            <span class="lumi-sidebar__menu-text">{{ child.label }}</span>
            <span :id="lockHintId(child.id, 'flyout')" hidden>{{ child.locked.hint }}</span>
          </button>
          <a
            v-else-if="child.href"
            :href="child.href"
            class="lumi-sidebar__menu-item"
            :class="{ 'lumi-sidebar__menu-item--current': child.id === activeId }"
            role="menuitem"
            :aria-current="child.id === activeId ? 'page' : undefined"
            rel="noopener"
            @click="closeFlyout"
          >
            <component :is="child.icon" v-if="child.icon" class="lumi-sidebar__menu-glyph" aria-hidden="true" />
            <span class="lumi-sidebar__menu-text">{{ child.label }}</span>
          </a>
          <button
            v-else
            type="button"
            class="lumi-sidebar__menu-item"
            :class="{ 'lumi-sidebar__menu-item--current': child.id === activeId }"
            role="menuitem"
            :aria-current="child.id === activeId ? 'page' : undefined"
            @click="onFlyoutNavigate(child.id)"
          >
            <component :is="child.icon" v-if="child.icon" class="lumi-sidebar__menu-glyph" aria-hidden="true" />
            <span class="lumi-sidebar__menu-text">{{ child.label }}</span>
          </button>
        </template>
      </div>
    </Teleport>

    <!-- Locked entries (#1028): the hover hint and the switch dialog, teleported
         to <body> — see `lockItem`. -->
    <Teleport to="body">
      <div
        v-if="lockHint"
        class="lumi-sidebar__lock-hint"
        aria-hidden="true"
        :style="{ top: `${lockHint.top}px`, left: `${lockHint.left}px` }"
      >
        {{ lockHint.text }}
      </div>
      <div v-if="lockItem" class="lumi-sidebar__menu-backdrop" @click="closeLock" />
      <div
        v-if="lockItem && lockItem.locked"
        ref="lockRef"
        class="lumi-sidebar__lock"
        role="dialog"
        tabindex="-1"
        :aria-labelledby="`${lockUid}-title`"
        :style="{ top: `${lockPos.top}px`, left: `${lockPos.left}px` }"
        data-test="sidebar-lock-dialog"
      >
        <div :id="`${lockUid}-title`" class="lumi-sidebar__lock-title">
          <component :is="lockItem.icon" v-if="lockItem.icon" aria-hidden="true" />
          <span>{{ lockItem.label }}</span>
        </div>
        <p class="lumi-sidebar__lock-body">{{ lockItem.locked.body }}</p>
        <div class="lumi-sidebar__lock-actions">
          <Button size="sm" data-test="sidebar-lock-switch" @click="onUnlock">{{ lockItem.locked.actionLabel }}</Button>
          <Button size="sm" variant="ghost" @click="closeLock">{{ lockedCloseLabel }}</Button>
        </div>
        <p v-if="lockedWhereLabel" class="lumi-sidebar__lock-where">
          <LumiIcon name="role" width="15" height="15" stroke-width="2" aria-hidden="true" />
          <span>{{ lockedWhereLabel }}</span>
        </p>
      </div>
    </Teleport>

    <!-- Account menu (spec §4), teleported to <body> — see `menuOpen`. -->
    <Teleport to="body">
      <div v-if="menuOpen" class="lumi-sidebar__menu-backdrop" @click="closeMenu" />
      <div
        v-if="menuOpen"
        ref="menuContainerRef"
        class="lumi-sidebar__menu"
        role="menu"
        tabindex="-1"
        :style="{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }"
      >
        <div class="lumi-sidebar__menu-head">
          <div v-if="name || !email" class="lumi-sidebar__menu-name" :title="displayName">{{ displayName }}</div>
          <div v-if="email" class="lumi-sidebar__menu-email" :title="email">{{ email }}</div>
        </div>

        <template v-if="profileHref || settingsHref">
          <a v-if="profileHref" :href="profileHref" class="lumi-sidebar__menu-item lumi-sidebar__menu-profile" role="menuitem" @click="onProfile">
            <LumiIcon name="me" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
            <span class="lumi-sidebar__menu-text">{{ profileLabel }}</span>
          </a>
          <a v-if="settingsHref" :href="settingsHref" class="lumi-sidebar__menu-item lumi-sidebar__menu-settings" role="menuitem" rel="noopener" @click="onSettings">
            <LumiIcon name="settings" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
            <span class="lumi-sidebar__menu-text">{{ settingsLabel }}</span>
            <LumiIcon v-if="settingsIsExternal" name="open-external" class="lumi-sidebar__menu-external" width="14" height="14" stroke-width="2" />
          </a>
          <div class="lumi-sidebar__menu-divider" role="separator" />
        </template>

        <!-- Rolle: the current role; a disclosure listing the others when there
             is more than one to hold. -->
        <template v-if="roleSwitchable || roleLabel">
          <button
            v-if="roleSwitchable"
            type="button"
            class="lumi-sidebar__menu-item lumi-sidebar__menu-role"
            role="menuitem"
            :aria-expanded="openGroup === 'role'"
            @click="toggleGroup('role')"
          >
            <LumiIcon name="role" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
            <span class="lumi-sidebar__menu-text">{{ roleMenuLabel }}</span>
            <span class="lumi-sidebar__menu-value">{{ currentRoleLabel }}</span>
            <LumiIcon name="next" class="lumi-sidebar__menu-chevron" :class="{ 'lumi-sidebar__menu-chevron--open': openGroup === 'role' }" width="14" height="14" stroke-width="2.5" />
          </button>
          <div v-else class="lumi-sidebar__menu-item lumi-sidebar__menu-item--static lumi-sidebar__menu-role">
            <LumiIcon name="role" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
            <span class="lumi-sidebar__menu-text">{{ roleMenuLabel }}</span>
            <span class="lumi-sidebar__menu-value">{{ currentRoleLabel }}</span>
          </div>
          <div v-if="roleSwitchable && openGroup === 'role'" class="lumi-sidebar__menu-list" role="group" :aria-label="roleMenuLabel">
            <button
              v-for="r in roles"
              :key="String(r.value)"
              type="button"
              class="lumi-sidebar__menu-option"
              :class="{ 'lumi-sidebar__menu-option--current': String(r.value) === String(activeRole) }"
              role="menuitemradio"
              :aria-checked="String(r.value) === String(activeRole)"
              :title="r.label"
              @click="onMenuRole(r.value)"
            >
              {{ r.label }}
            </button>
          </div>
          <div class="lumi-sidebar__menu-divider" role="separator" />
        </template>

        <!-- Design + Sprache, each showing its current value. -->
        <button v-if="theme" type="button" class="lumi-sidebar__menu-item lumi-sidebar__menu-theme" role="menuitem" @click="onToggleTheme">
          <LumiIcon name="theme" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
          <span class="lumi-sidebar__menu-text">{{ themeMenuLabel }}</span>
          <span class="lumi-sidebar__menu-value">{{ currentThemeLabel }}</span>
        </button>
        <template v-if="locales && locales.length">
          <button
            type="button"
            class="lumi-sidebar__menu-item lumi-sidebar__menu-language"
            role="menuitem"
            :aria-expanded="openGroup === 'locale'"
            @click="toggleGroup('locale')"
          >
            <LumiIcon name="language" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
            <span class="lumi-sidebar__menu-text">{{ languageMenuLabel }}</span>
            <span class="lumi-sidebar__menu-value">{{ currentLocaleLabel }}</span>
            <LumiIcon name="next" class="lumi-sidebar__menu-chevron" :class="{ 'lumi-sidebar__menu-chevron--open': openGroup === 'locale' }" width="14" height="14" stroke-width="2.5" />
          </button>
          <div v-if="openGroup === 'locale'" class="lumi-sidebar__menu-list" role="group" :aria-label="languageMenuLabel">
            <button
              v-for="l in locales"
              :key="String(l.value)"
              type="button"
              class="lumi-sidebar__menu-option"
              :class="{ 'lumi-sidebar__menu-option--current': String(l.value) === String(locale) }"
              role="menuitemradio"
              :aria-checked="String(l.value) === String(locale)"
              @click="onMenuLocale(l.value)"
            >
              {{ l.label }}
            </button>
          </div>
        </template>
        <div v-if="theme || (locales && locales.length)" class="lumi-sidebar__menu-divider" role="separator" />

        <button type="button" class="lumi-sidebar__menu-item lumi-sidebar__menu-support" role="menuitem" @click="onSupport">
          <LumiIcon name="support" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
          <span class="lumi-sidebar__menu-text">{{ supportLabel }}</span>
        </button>
        <button
          type="button"
          class="lumi-sidebar__menu-item lumi-sidebar__menu-logout"
          :class="{ 'lumi-sidebar__menu-logout--disabled': logoutDisabled }"
          role="menuitem"
          :aria-label="logoutLabel"
          :aria-disabled="logoutDisabled ? 'true' : undefined"
          :title="logoutDisabled && logoutHint ? logoutHint : logoutLabel"
          @click="onLogout"
        >
          <LumiIcon name="sign-out" class="lumi-sidebar__menu-glyph" width="16" height="16" stroke-width="2" />
          <span class="lumi-sidebar__menu-text">{{ logoutLabel }}</span>
        </button>
      </div>
    </Teleport>
  </aside>
</template>

<style>
/* The nav column lies open on the sand canvas — no surface, no hairline (spec §4). */
.lumi-sidebar{width:var(--sidebar-w);background:none;display:flex;flex-direction:column;height:100%;font-family:var(--font-body);flex-shrink:0;padding:0 var(--space-3);}
.lumi-sidebar__logo{height:var(--topbar-h);display:flex;align-items:center;justify-content:space-between;gap:var(--space-2);padding:0 var(--space-2);flex-shrink:0;}
.lumi-sidebar__home{display:flex;align-items:center;min-width:0;border:none;background:none;padding:0;cursor:pointer;text-decoration:none;color:inherit;font-family:inherit;}
/* The mark: a mask over the SVG filled with the logo colour, 26px high. The
   box is height-driven with the width set from the wordmark's OWN aspect
   ratio (1294:628, issue 464) rather than a fixed px guess tuned to whichever
   logo shipped last — `mask-size:contain` only stops the artwork from being
   stretched, it does nothing about a box sized for a different shape leaving
   dead space (or, the other way round, clipping a wider one). */
.lumi-sidebar__mark{display:block;height:26px;width:auto;aspect-ratio:1294/628;background:var(--logo-color);mask-repeat:no-repeat;mask-size:contain;mask-position:left center;-webkit-mask-repeat:no-repeat;-webkit-mask-size:contain;-webkit-mask-position:left center;}
.lumi-sidebar__wordmark{font-family:var(--font-display);font-size:18px;color:var(--text-heading);}
.lumi-sidebar__dot{color:var(--accent);}
.lumi-sidebar__history{display:flex;align-items:center;gap:2px;flex-shrink:0;}
.lumi-sidebar__history-btn{display:flex;align-items:center;justify-content:center;width:28px;height:28px;border:none;background:none;border-radius:var(--radius-md);color:var(--text-secondary);cursor:pointer;}
.lumi-sidebar__history-btn:hover:not(:disabled){color:var(--text-heading);background:var(--surface-wash);}
.lumi-sidebar__history-btn:disabled{color:var(--text-tertiary);opacity:.45;cursor:default;}
.lumi-sidebar__badge{padding:var(--space-1) 0 var(--space-2);}
/* A module app's sidebar wears its module colour (tokens: --nav-module-*); Home
   (suite) and the admin console keep the lavender highlight. Scoped to the
   sidebar, so nothing outside it changes colour. */
.lumi-sidebar[data-module='people']{--nav-module:var(--nav-module-people);}
.lumi-sidebar[data-module='ats']{--nav-module:var(--nav-module-ats);}
.lumi-sidebar[data-module='time']{--nav-module:var(--nav-module-time);}
.lumi-sidebar[data-module='apprentice']{--nav-module:var(--nav-module-apprentice);}
.lumi-sidebar:is([data-module='people'],[data-module='ats'],[data-module='time'],[data-module='apprentice']){--nav-highlight:color-mix(in oklab,var(--nav-module) 50%,var(--text-heading));--nav-spine:var(--nav-highlight);}
/* The module badge wears the sidebar highlight here only — the launcher it opens
   and the plain badge on the workspace home cards keep their own colours. */
.lumi-sidebar__badge .lumi-modbadge{color:var(--nav-highlight);}
.lumi-sidebar__badge .lumi-modbadge__icon{background:var(--nav-highlight);color:var(--text-inverse);}
.lumi-sidebar:is([data-module='people'],[data-module='ats'],[data-module='time'],[data-module='apprentice']) .lumi-sidebar__badge .lumi-modbadge__icon{background:var(--nav-module);color:var(--text-heading);}
/* …except a clay piece (issue 835), which keeps the pale wash ModuleBadge gives it. */
.lumi-sidebar .lumi-sidebar__badge .lumi-modbadge .lumi-modbadge__icon.lumi-modbadge__icon--art{background:color-mix(in oklab,var(--nav-module) 20%,var(--surface-card));}
.lumi-sidebar--locked .lumi-sidebar__nav,.lumi-sidebar--locked .lumi-sidebar__badge{opacity:.45;pointer-events:none;}
.lumi-sidebar__nav{flex:1;padding:var(--space-2) 0;display:flex;flex-direction:column;gap:2px;overflow-y:auto;overflow-x:hidden;}
/* Nav item: 14px, 10px radius; the active one is a wash pill with the panel-edge
   ring and a 3px spine at its left edge (spec §4). The 1px ring is reserved on
   every row so the active row does not shift its neighbours. */
.lumi-sidebar__item{position:relative;display:flex;align-items:center;gap:12px;padding:8px 10px 8px 12px;border-radius:var(--radius-md);border:1px solid transparent;color:var(--text-body);font-size:var(--text-sm);font-weight:var(--weight-medium);cursor:pointer;background:none;width:100%;text-align:left;font-family:inherit;transition:background-color var(--dur-fast),color var(--dur-fast);text-decoration:none;}
.lumi-sidebar__item:hover{background:var(--surface-wash);color:var(--text-heading);text-decoration:none;}
.lumi-sidebar__item--active,.lumi-sidebar__item--active:hover{background:var(--surface-wash);border-color:var(--panel-edge);color:var(--nav-highlight);font-weight:var(--weight-semibold);}
.lumi-sidebar__item--active::before{content:'';position:absolute;left:-1px;top:8px;bottom:8px;width:3px;border-radius:0 2px 2px 0;background:var(--nav-spine);}
.lumi-sidebar__item svg{width:18px;height:18px;flex-shrink:0;color:var(--text-secondary);}
.lumi-sidebar__item:hover svg{color:var(--text-heading);}
.lumi-sidebar__item--active svg,.lumi-sidebar__item--active:hover svg{color:var(--nav-highlight);}
.lumi-sidebar:is([data-module='people'],[data-module='ats'],[data-module='time'],[data-module='apprentice']) .lumi-sidebar__item--active{border-color:color-mix(in oklab,var(--nav-module) 35%,var(--surface-card));}
.lumi-sidebar__label{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
/* The nav indicator (#444). NO COLOUR of its own: `currentColor` at reduced
   opacity, so it reads as a note about that page rather than an alarm about the
   app — the sidebar has no other red, and a page that merely wants filling in has
   not earned one. Sits after the label, pushed right so the indicators on
   different rows line up down the nav. */
.lumi-sidebar__indicator{width:14px;height:14px;flex-shrink:0;margin-left:auto;opacity:0.55;}
/* Count pill (#548): the Tabs counter's pill, so a waiting count reads the same in the nav as in a tab. */
.lumi-sidebar__count{margin-left:auto;flex-shrink:0;min-width:18px;padding:0 6px;border-radius:var(--radius-pill);background:var(--pill-ring);color:var(--text-heading);font-size:11px;font-weight:var(--weight-medium);line-height:16px;text-align:center;font-variant-numeric:tabular-nums;}
.lumi-sidebar__indicator + .lumi-sidebar__count{margin-left:6px;}
.lumi-sidebar__sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0;}
/* Section labels sit on the sand canvas, where tertiary reads 3.0:1 and
   secondary 3.6:1 in light — body ink is the first step that clears 4.5:1
   (6.6:1); at 11px caps it still reads quieter than the 14px items. */
.lumi-sidebar__section{display:flex;align-items:center;justify-content:space-between;width:100%;padding:var(--space-4) 12px var(--space-1);margin-top:var(--space-1);font-size:var(--text-caps);font-weight:var(--weight-semibold);letter-spacing:var(--tracking-caps);text-transform:uppercase;color:var(--text-body);background:none;border:none;cursor:pointer;font-family:inherit;text-align:left;}
.lumi-sidebar__section:hover{color:var(--text-heading);}
.lumi-sidebar__section-end{display:flex;align-items:center;gap:6px;flex-shrink:0;}
.lumi-sidebar__section-end .lumi-sidebar__count{margin-left:0;}
.lumi-sidebar__chevron{transition:transform var(--dur-fast) var(--ease-out);flex-shrink:0;}
.lumi-sidebar__section--open .lumi-sidebar__chevron{transform:rotate(180deg);}
.lumi-sidebar__group{display:flex;flex-direction:column;gap:2px;}
.lumi-sidebar__footer{padding:var(--space-2) 0 var(--space-3);display:flex;flex-direction:column;gap:var(--space-2);flex-shrink:0;}
.lumi-sidebar__footer-extra{display:flex;flex-direction:column;}
/* The account row: the avatar on the nav-icon axis (same 12px inset as an item's
   glyph), name + role beside it, then the bell and Lumi, Lumi outermost. */
.lumi-sidebar__account-row{display:flex;align-items:center;gap:2px;}
.lumi-sidebar__avatar-btn{display:flex;align-items:center;gap:10px;flex:1;min-width:0;border:none;background:none;padding:6px 8px 6px 12px;border-radius:var(--radius-md);cursor:pointer;text-align:left;font-family:inherit;color:inherit;}
.lumi-sidebar__avatar-btn:hover{background:var(--surface-wash);}
.lumi-sidebar__account-text{display:flex;flex-direction:column;min-width:0;line-height:1.25;}
.lumi-sidebar__account-name{font-size:var(--text-sm);font-weight:var(--weight-semibold);color:var(--text-heading);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lumi-sidebar__account-role{font-size:var(--text-xs);color:var(--text-body);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lumi-sidebar__notifications{position:relative;display:flex;}
/* Round icon buttons (bell, Lumi). */
.lumi-sidebar__icon-btn{display:flex;align-items:center;justify-content:center;border:none;background:none;padding:0;width:32px;height:32px;border-radius:var(--radius-pill);cursor:pointer;color:var(--text-secondary);flex-shrink:0;}
.lumi-sidebar__icon-btn:hover{background:var(--surface-wash);color:var(--text-heading);}
.lumi-sidebar__notif-icon{position:relative;display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;}
/* Lumi: the glyph in a speech bubble (LumiChatGlyph), a size up from the bell
   (#1069). The bubble is drawn like the bell — its colour (currentColor), its
   1.5px line — and hovers like it: the round wash behind, the line darkening.
   The glyph's knockout halo follows the ground it is cut into. Open, the bubble
   fills, so a second click reads as "close this". */
.lumi-sidebar__lumi-btn{width:40px;height:40px;}
.lumi-sidebar__lumi-btn--open{color:var(--text-heading);}
.lumi-sidebar__lumi-btn:hover .lumi-chat-glyph__knockout{fill:var(--surface-wash);stroke:var(--surface-wash);}
/* Lifted 7px: the bubble's tail and the rays above it put the drawing's centre
   below the svg's, so centred as-is the bubble sat low against the bell and the name. */
.lumi-sidebar__lumi-icon{width:36px;height:36px;margin-bottom:7px;}
/* Unread count: coral (spec §2); besides the Lumi glyph, the one place the sidebar raises its voice. */
.lumi-sidebar__notif-badge{position:absolute;top:2px;right:2px;min-width:14px;height:14px;padding:0 3px;border-radius:var(--radius-pill);background:var(--attention);color:var(--attention-fg);font-size:9px;font-weight:var(--weight-bold);line-height:14px;text-align:center;}
/* Quiet "not yet known" marker: a small neutral dot, visibly not a count. */
.lumi-sidebar__notif-badge--unknown{min-width:8px;width:8px;height:8px;padding:0;top:4px;right:4px;background:var(--text-tertiary);}
/* Notifications popover — teleported to <body>: fixed positioning with top/left
   set inline from the bell's rect and translateY(-100%) so the panel's BOTTOM
   edge sits at that point ("open upward from the bell"). */
.lumi-sidebar__notif-panel{position:fixed;transform:translateY(-100%);z-index:calc(var(--layer-popover) + 1);width:360px;max-width:calc(100vw - 24px);background:var(--surface-card);border:1px solid var(--border-default);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);overflow:hidden;}
.lumi-sidebar__menu-backdrop{position:fixed;inset:0;z-index:var(--layer-popover);}
/* Account menu — same anchoring recipe as the notifications panel. */
.lumi-sidebar__menu{position:fixed;transform:translateY(-100%);z-index:calc(var(--layer-popover) + 1);width:236px;max-width:calc(100vw - 24px);background:var(--surface-card);border:1px solid var(--border-default);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);padding:var(--space-2);display:flex;flex-direction:column;gap:1px;}
.lumi-sidebar__menu-head{padding:6px 10px 8px;min-width:0;}
.lumi-sidebar__menu-name{font-size:var(--text-sm);font-weight:var(--weight-semibold);color:var(--text-heading);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lumi-sidebar__menu-email{font-size:var(--text-xs);color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lumi-sidebar__menu-divider{height:1px;background:var(--border-default);margin:4px 0;}
.lumi-sidebar__menu-item{display:flex;align-items:center;gap:10px;width:100%;padding:7px 10px;border:none;border-radius:var(--radius-sm);background:none;color:var(--text-body);font-family:inherit;font-size:var(--text-sm);text-align:left;text-decoration:none;cursor:pointer;}
.lumi-sidebar__menu-item:hover{background:var(--surface-wash);color:var(--text-heading);text-decoration:none;}
.lumi-sidebar__menu-item--static,.lumi-sidebar__menu-item--static:hover{cursor:default;background:none;color:var(--text-body);}
.lumi-sidebar__menu-glyph{flex-shrink:0;color:var(--text-secondary);}
.lumi-sidebar__menu-text{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lumi-sidebar__menu-value{font-size:var(--text-xs);color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:45%;}
.lumi-sidebar__menu-chevron,.lumi-sidebar__menu-external{flex-shrink:0;color:var(--text-tertiary);}
.lumi-sidebar__menu-chevron{transition:transform var(--dur-fast) var(--ease-out);}
.lumi-sidebar__menu-chevron--open{transform:rotate(90deg);}
/* The inline list a disclosure opens: indented under its row's label. */
.lumi-sidebar__menu-list{display:flex;flex-direction:column;gap:1px;padding:2px 0 4px 26px;}
/* A role name longer than the 236px menu (var(--sidebar-menu-w) territory)
   ellipsises rather than wrapping or overflowing the panel; the full name is
   still reachable through the native title (issue 535). This is a text-only
   option (no icon/glyph to align), and text-overflow: ellipsis does not
   render on a flex container — the text becomes an anonymous flex item that
   only clips — so this stays a block element. */
.lumi-sidebar__menu-option{display:block;width:100%;padding:5px 10px;border:none;border-radius:var(--radius-sm);background:none;color:var(--text-body);font-family:inherit;font-size:var(--text-sm);text-align:left;cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.lumi-sidebar__menu-option:hover{background:var(--surface-wash);color:var(--text-heading);}
.lumi-sidebar__menu-option--current{background:var(--selected-bg);color:var(--text-heading);font-weight:var(--weight-medium);}
.lumi-sidebar__menu-logout--disabled,.lumi-sidebar__menu-logout--disabled:hover{opacity:.5;cursor:not-allowed;background:none;}
/* Locked entry (#1028): the row keeps its place and its focus ring but recedes —
   tertiary ink, the glyph and label at reduced strength, no hover fill. It is not
   dead (a click explains), so the cursor asks rather than refuses. */
.lumi-sidebar__item--locked,.lumi-sidebar__item--locked:hover{background:none;color:var(--text-tertiary);cursor:help;}
.lumi-sidebar__item--locked svg,.lumi-sidebar__item--locked:hover svg{color:var(--text-tertiary);}
.lumi-sidebar__item--locked > svg,.lumi-sidebar__item--locked .lumi-sidebar__label,.lumi-sidebar__item--locked .lumi-sidebar__initial{opacity:.6;transition:opacity var(--dur-fast);}
.lumi-sidebar__item--locked:hover > svg,.lumi-sidebar__item--locked:hover .lumi-sidebar__label,.lumi-sidebar__item--locked[aria-expanded='true'] > svg,.lumi-sidebar__item--locked[aria-expanded='true'] .lumi-sidebar__label{opacity:.85;}
.lumi-sidebar__menu-item--locked,.lumi-sidebar__menu-item--locked:hover{background:none;color:var(--text-tertiary);cursor:help;}
.lumi-sidebar__menu-item--locked .lumi-sidebar__menu-glyph{color:var(--text-tertiary);opacity:.6;}
.lumi-sidebar__lock-hint{position:fixed;transform:translateY(-50%);z-index:calc(var(--layer-popover) + 2);max-width:260px;padding:7px 10px;border-radius:var(--radius-md);background:var(--btn-primary-bg);color:var(--btn-primary-fg);font-size:var(--text-xs);line-height:1.4;box-shadow:var(--shadow-lg);pointer-events:none;}
.lumi-sidebar__lock{position:fixed;z-index:calc(var(--layer-popover) + 1);width:300px;max-width:calc(100vw - 24px);box-sizing:border-box;background:var(--surface-card);border:1px solid var(--border-default);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);padding:var(--space-3) var(--space-4);display:flex;flex-direction:column;gap:var(--space-2);}
.lumi-sidebar__lock-title{display:flex;align-items:center;gap:8px;font-size:var(--text-sm);font-weight:var(--weight-semibold);color:var(--text-heading);}
.lumi-sidebar__lock-title svg{width:16px;height:16px;flex-shrink:0;color:var(--text-secondary);}
.lumi-sidebar__lock-body{margin:0;font-size:var(--text-sm);line-height:1.45;color:var(--text-body);}
.lumi-sidebar__lock-actions{display:flex;flex-wrap:wrap;gap:var(--space-2);}
.lumi-sidebar__lock-where{display:flex;align-items:flex-start;gap:8px;margin:2px 0 0;padding-top:var(--space-2);border-top:1px solid var(--border-default);font-size:var(--text-xs);line-height:1.4;color:var(--text-secondary);}
.lumi-sidebar__lock-where svg{flex-shrink:0;margin-top:1px;}
/* One pulse on the account row when the dialog opens: that is where the role is
   switched any time. Reduced motion gets a still ring instead. */
.lumi-sidebar__avatar-btn--pulse{animation:lumi-sidebar-pulse 0.8s var(--ease-out) 2;}
@keyframes lumi-sidebar-pulse{0%{box-shadow:0 0 0 0 color-mix(in oklab,var(--nav-highlight) 55%,transparent);}100%{box-shadow:0 0 0 10px transparent;}}
@media (prefers-reduced-motion: reduce){.lumi-sidebar__avatar-btn--pulse{animation:none;box-shadow:0 0 0 2px var(--nav-highlight);}}
/* ── The folded rail (#540): 72px, icons only. Labels stay in the DOM, visually
   hidden, so every row keeps its accessible name; the native title is the
   tooltip. The foot stacks Lumi, the bell and the avatar; the footer extras
   (usage, tenant switcher) wait for the open sidebar. ── */
.lumi-sidebar--rail{width:var(--sidebar-rail-w);}
.lumi-sidebar--rail .lumi-sidebar__logo{height:auto;flex-direction:column;justify-content:center;gap:var(--space-3);padding:15px 0 4px;}
.lumi-sidebar--rail .lumi-sidebar__mark{height:22px;}
.lumi-sidebar--rail .lumi-sidebar__wordmark{font-size:var(--text-xs);}
.lumi-sidebar--rail .lumi-sidebar__history-btn{width:24px;height:24px;}
.lumi-sidebar--rail .lumi-sidebar__badge .lumi-modbadge{justify-content:center;gap:0;padding:6px;}
.lumi-sidebar--rail .lumi-sidebar__badge .lumi-modbadge__name,.lumi-sidebar--rail .lumi-sidebar__badge .lumi-modbadge__caret{display:none;}
.lumi-sidebar--rail .lumi-sidebar__item{justify-content:center;gap:0;padding:9px 0;}
/* A scrolling rail hides its scrollbar: a classic one takes its width out of the
   nav alone, and every icon slid off the module badge's axis above (#1069). */
.lumi-sidebar--rail .lumi-sidebar__nav{scrollbar-width:none;}
.lumi-sidebar--rail .lumi-sidebar__nav::-webkit-scrollbar{display:none;}
.lumi-sidebar--rail .lumi-sidebar__label{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;}
.lumi-sidebar--rail .lumi-sidebar__indicator{position:absolute;top:3px;right:5px;width:11px;height:11px;margin:0;}
.lumi-sidebar--rail .lumi-sidebar__count{position:absolute;top:1px;right:1px;min-width:15px;padding:0 4px;margin:0;font-size:10px;line-height:15px;}
.lumi-sidebar__initial{display:flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:6px;background:var(--surface-wash);box-shadow:inset 0 0 0 1px var(--pill-ring);font-size:var(--text-xs);font-weight:var(--weight-semibold);line-height:1;}
.lumi-sidebar__rail-divider{display:block;flex-shrink:0;width:28px;height:1px;margin:10px auto;background:var(--pill-ring);}
.lumi-sidebar__rail-section--open{background:var(--surface-wash);}
.lumi-sidebar--rail .lumi-sidebar__account-row{flex-direction:column-reverse;align-items:center;gap:4px;}
.lumi-sidebar--rail .lumi-sidebar__avatar-btn{flex:none;padding:4px;}
.lumi-sidebar--rail .lumi-sidebar__account-text{display:none;}
.lumi-sidebar__flyout{position:fixed;z-index:calc(var(--layer-popover) + 1);width:236px;max-width:calc(100vw - 24px);max-height:calc(100vh - 16px);overflow-y:auto;box-sizing:border-box;background:var(--surface-card);border:1px solid var(--border-default);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);padding:var(--space-2);display:flex;flex-direction:column;gap:1px;}
.lumi-sidebar__flyout-head{padding:6px 10px 8px;font-size:var(--text-caps);font-weight:var(--weight-semibold);letter-spacing:var(--tracking-caps);text-transform:uppercase;color:var(--text-body);}
.lumi-sidebar__flyout .lumi-sidebar__menu-glyph{width:16px;height:16px;}
.lumi-sidebar__menu-item--current,.lumi-sidebar__menu-item--current:hover{background:var(--selected-bg);color:var(--text-heading);font-weight:var(--weight-medium);}
</style>
